import React, { useContext } from "react";
import { Grid, GridColumn, GridRow } from "sprint-common-web";

import { LanguagesDataProvider } from "../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { MuxerType } from "../../../common/types/MuxerType";
import { LanguageSpecificationType } from "../../common/types/LanguageSpecificationType";
import { MuxSpecificationDashboardContext } from "./context/MuxSpecificationDashboardContext";
import { MuxSpecificationDashboardProvider } from "./context/MuxSpecificationDashboardProvider";
import { GpacMuxSpecificationInfoContent } from "./info/GpacMuxSpecificationInfoContent";
import { ManzanitaMuxSpecificationInfoContent } from "./info/ManzanitaMuxSpecificationInfoContent";
import { MuxSpecificationInfoSection } from "./info/MuxSpecificationInfoSection";
import { ManzanitaMuxSpecificationLanguageMappingsSection } from "./language-mapping/ManzanitaMuxSpecificationLanguageMappingsSection";
import { MuxSpecificationDashboardHeader } from "./MuxSpecificationDashboardHeader";
import { MuxSpecificationDashboardItem } from "./MuxSpecificationDashboardItem";
import { GpacMuxSpecificationTechnicalContent } from "./technical/GpacMuxSpecificationTechnicalContent";
import { ManzanitaMuxSpecificationTechnicalContent } from "./technical/ManzanitaMuxSpecificationTechnicalContent";
import { MuxSpecificationTechnicalSection } from "./technical/MuxSpecificationTechnicalSection";

export const MuxSpecificationDashboardPage: React.FC = () => {
    return (
        <LanguagesDataProvider>
            <MuxSpecificationDashboardProvider>
                <MuxSpecificationDashboardHeader />
                <MuxSpecificationDashboard />
            </MuxSpecificationDashboardProvider>
        </LanguagesDataProvider>
    );
};

const MuxSpecificationDashboard: React.FC = () => {
    const { muxSpecificationDashboardItem } = useContext(MuxSpecificationDashboardContext);

    const { infoSectionContent, technicalSectionContent, additionalSection } = getSections(
        muxSpecificationDashboardItem
    );

    return (
        <Grid>
            <GridRow>
                <GridColumn sm={4}>
                    <MuxSpecificationInfoSection height={350} content={infoSectionContent} />
                </GridColumn>
                <GridColumn sm={4}>
                    <MuxSpecificationTechnicalSection height={350} content={technicalSectionContent} />
                </GridColumn>
            </GridRow>
            {additionalSection}
        </Grid>
    );
};

const getSections = (
    muxSpecification: MuxSpecificationDashboardItem
): {
    infoSectionContent: React.ReactNode;
    technicalSectionContent: React.ReactNode | null;
    additionalSection: React.ReactNode | null;
} => {
    if (muxSpecification.muxerType === MuxerType.Manzanita) {
        return {
            infoSectionContent: <ManzanitaMuxSpecificationInfoContent />,
            technicalSectionContent: <ManzanitaMuxSpecificationTechnicalContent />,
            additionalSection:
                muxSpecification.languageSpecification.type === LanguageSpecificationType.Dictionary ? (
                    <GridRow>
                        <GridColumn sm={8}>
                            <ManzanitaMuxSpecificationLanguageMappingsSection height={550} />
                        </GridColumn>
                    </GridRow>
                ) : null
        };
    } else if (muxSpecification.muxerType === MuxerType.GPAC) {
        return {
            infoSectionContent: <GpacMuxSpecificationInfoContent />,
            technicalSectionContent: <GpacMuxSpecificationTechnicalContent />,
            additionalSection: null
        };
    }
    throw Error("Unknown muxer type");
};
